import React, { useState, useEffect, useCallback } from 'react';
import { Wifi, WifiOff, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';

const NetworkMonitor = () => {
  // eslint-disable-next-line no-unused-vars
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [networkStatus, setNetworkStatus] = useState('online');
  const [showWarning, setShowWarning] = useState(false);
  const [lastOfflineTime, setLastOfflineTime] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  // Check actual connectivity by making an authenticated Supabase query
  const checkConnectivity = useCallback(async () => {
    try {
      // Use AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      // Make a lightweight authenticated query to check connectivity
      // Using head:true and limit:1 makes this extremely lightweight
      const { error } = await supabase
        .from('projects')
        .select('id', { count: 'exact', head: true })
        .limit(1)
        .abortSignal(controller.signal);

      clearTimeout(timeoutId);

      // If no error, connection is good
      if (!error) {
        setNetworkStatus('online');
        setShowWarning(false);
        setReconnectAttempts(0);
        return true;
      } else {
        // Check if it's an auth error vs network error
        if (error.message?.includes('JWT') || error.message?.includes('auth')) {
          // Auth error means connection is fine, just need to refresh
          setNetworkStatus('online');
          setShowWarning(false);
          setReconnectAttempts(0);
          return true;
        } else {
          // Network error
          setNetworkStatus('degraded');
          console.warn('[NetworkMonitor] Connection degraded:', error.message);
          return false;
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setNetworkStatus('slow');
        console.warn('[NetworkMonitor] Connection slow (timeout)');
      } else {
        setNetworkStatus('offline');
        console.error('[NetworkMonitor] Connection check failed:', error.message);
      }
      return false;
    }
  }, []);

  // Handle online event
  const handleOnline = useCallback(async () => {
    console.log('[NetworkMonitor] Browser reports online');
    setIsOnline(true);

    // Show reconnecting message
    setNetworkStatus('reconnecting');
    setShowWarning(true);

    // Verify actual connectivity
    const isActuallyOnline = await checkConnectivity();

    if (isActuallyOnline) {
      // Calculate offline duration
      if (lastOfflineTime) {
        const offlineDuration = Date.now() - lastOfflineTime;
        const minutes = Math.floor(offlineDuration / 60000);
        const seconds = Math.floor((offlineDuration % 60000) / 1000);
        console.log(`[NetworkMonitor] Was offline for ${minutes}m ${seconds}s`);
      }

      // Show success message briefly
      setNetworkStatus('online');
      setTimeout(() => {
        setShowWarning(false);
      }, 3000);
    } else {
      // Still having issues
      setNetworkStatus('degraded');
      setReconnectAttempts(prev => prev + 1);
    }
  }, [checkConnectivity, lastOfflineTime]);

  // Handle offline event
  const handleOffline = useCallback(() => {
    console.log('[NetworkMonitor] Browser reports offline');
    setIsOnline(false);
    setNetworkStatus('offline');
    setShowWarning(true);
    setLastOfflineTime(Date.now());
  }, []);

  // Setup event listeners
  useEffect(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial connectivity check
    if (navigator.onLine) {
      checkConnectivity();
    } else {
      handleOffline();
    }

    // Periodic connectivity check every 30 seconds when online
    const intervalId = setInterval(() => {
      if (navigator.onLine) {
        checkConnectivity();
      }
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(intervalId);
    };
  }, [handleOnline, handleOffline, checkConnectivity]);

  // Attempt reconnection when degraded
  useEffect(() => {
    if (networkStatus === 'degraded' && reconnectAttempts > 0) {
      const retryDelay = Math.min(Math.pow(2, reconnectAttempts) * 1000, 30000); // Max 30s
      console.log(`[NetworkMonitor] Retrying connection in ${retryDelay}ms (attempt ${reconnectAttempts})`);

      const timeoutId = setTimeout(() => {
        checkConnectivity();
      }, retryDelay);

      return () => clearTimeout(timeoutId);
    }
  }, [networkStatus, reconnectAttempts, checkConnectivity]);

  // Don't show anything if online and no warning
  if (!showWarning && networkStatus === 'online') {
    return null;
  }

  const getStatusConfig = () => {
    switch (networkStatus) {
      case 'offline':
        return {
          icon: WifiOff,
          message: 'No internet connection',
          detail: 'Your changes will be saved when connection is restored',
          bgColor: 'bg-red-500',
          textColor: 'text-white'
        };
      case 'slow':
        return {
          icon: AlertTriangle,
          message: 'Slow connection detected',
          detail: 'Some features may be unavailable',
          bgColor: 'bg-yellow-500',
          textColor: 'text-white'
        };
      case 'degraded':
        return {
          icon: AlertTriangle,
          message: 'Connection issues',
          detail: `Attempting to reconnect... (${reconnectAttempts} attempts)`,
          bgColor: 'bg-orange-500',
          textColor: 'text-white'
        };
      case 'reconnecting':
        return {
          icon: Wifi,
          message: 'Reconnecting...',
          detail: 'Verifying connection to server',
          bgColor: 'bg-blue-500',
          textColor: 'text-white'
        };
      case 'online':
        return {
          icon: Wifi,
          message: 'Connection restored',
          detail: 'You are back online',
          bgColor: 'bg-green-500',
          textColor: 'text-white'
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  const Icon = config.icon;

  return (
    <>
      <style>{`
        @keyframes network-monitor-slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .network-monitor-animate-slide-up {
          animation: network-monitor-slide-up 0.3s ease-out;
        }
      `}</style>
      <div
        className={`fixed bottom-4 left-1/2 -translate-x-1/2 ${config.bgColor} ${config.textColor} px-4 py-3 rounded-lg shadow-lg flex items-center space-x-3 z-[9999] network-monitor-animate-slide-up max-w-sm`}
        style={{
          position: 'fixed',
          bottom: '1rem',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999
        }}
        data-version="2025-10-06-v3"
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        <div>
          <p className="font-semibold text-sm">{config.message}</p>
          {config.detail && (
            <p className="text-xs opacity-90 mt-1">{config.detail}</p>
          )}
        </div>
      </div>
    </>
  );
};

export default NetworkMonitor;